import { generateUUID } from '@/utils'
import { Image, Tooltip } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import AmrPlayer from '../../audio/AmrPlayer'
import { PauseSvg } from '../../audio/PauseSvg'
import { AudioPlaySvg } from '../../audio/PlaySvg'
import { RunningSvg } from '../../audio/RunningSvg'
import '../css/wanxiangUpload.less'
import { formatFileName, getFileIconByFileName, getFileType, isUrlExpired } from '../utils'

/**
 * 通义万相 material_info 展示组件
 *
 * 用于展示通义万相模型上传的图片、视频、音频文件
 *
 * @param materialInfo - 文件信息数组，包含 type、name、url
 * @param hasText - 是否有文本内容（用于调整间距）
 * @param audioPlayingId - 当前播放的音频ID
 * @param setAudioPlayingId - 设置当前播放的音频ID
 */
export default function MaterialInfoDisplay({
  materialInfo,
  hasText,
  audioPlayingId,
  setAudioPlayingId,
  type = ''
}: {
  materialInfo: Array<{ type: string; name: string; url: string; is_valid?: number; thumbnail_url?: string }>
  hasText: boolean
  audioPlayingId: string | null
  setAudioPlayingId: (id: string | null) => void
  type?: string
}) {
  // 为每个音频项生成唯一的 ID（使用 useMemo 确保稳定）
  const audioIds = useMemo(() => {
    return materialInfo
      .map((_, index) => (materialInfo[index].type === 'audio' ? generateUUID(16) : null))
      .filter(Boolean) as string[]
  }, [materialInfo])

  let audioIndex = 0

  return (
    <div className={hasText ? 'mt-[10px]' : ''}>
      {materialInfo.map((material, index) => {
        // 判断链接是否过期（优先使用 is_valid，如果没有则通过 URL 判断）
        const isExpired = isUrlExpired(material.url)

        if (material.type === 'image') {
          // 图片展示
          return (
            <div key={`material-image-${index}`} className={index > 0 ? 'mt-[10px]' : ''}>
              {isExpired ? (
                <div className="expired_img flexRow flexCol">
                  <div className="relative mb-[6px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 39 39" fill="none">
                      <path
                        d="M7.46996 14.6259C7.51012 15.2016 7.72001 15.7525 8.07313 16.2089C8.42624 16.6654 8.90673 17.0069 9.45389 17.1904C10.001 17.3739 10.5903 17.3911 11.1472 17.2398C11.7042 17.0886 12.2037 16.7756 12.5829 16.3405C12.962 15.9054 13.2036 15.3677 13.2773 14.7953C13.3509 14.2229 13.2532 13.6415 12.9966 13.1246C12.74 12.6077 12.3359 12.1785 11.8354 11.8912C11.3349 11.6039 10.7605 11.4713 10.1847 11.5103C9.80173 11.5363 9.42766 11.6375 9.08387 11.8082C8.74008 11.9789 8.43333 12.2157 8.18117 12.5051C7.92902 12.7945 7.73641 13.1307 7.61437 13.4947C7.49233 13.8586 7.44326 14.243 7.46996 14.6259Z"
                        fill="#5964F3"
                        fillOpacity="0.52"
                      />
                      <path
                        d="M20.1248 21.7608L19.6297 21.0438L17.9466 23.0527L14.5372 19.7934L6.55277 26.0373L4.48319 11.3618C4.43182 11.1069 4.43503 10.844 4.49262 10.5904C4.55022 10.3367 4.66088 10.0982 4.81733 9.89047C4.97378 9.68272 5.17247 9.51047 5.40031 9.38507C5.62814 9.25967 5.87996 9.18395 6.13917 9.16292L15.4092 7.85666C15.4666 6.86498 15.6886 5.65134 15.7732 4.85449L4.77064 6.40508C3.74915 6.55237 2.82729 7.09747 2.206 7.92157C1.58471 8.74567 1.31437 9.78194 1.45388 10.8045L4.16861 30.1078C4.31586 31.1302 4.86166 32.0529 5.68683 32.6743C6.51199 33.2957 7.54951 33.5654 8.57284 33.4245L17.6448 32.147L20.1248 21.7608Z"
                        fill="url(#paint0_linear_4234_32212)"
                      />
                      <path
                        d="M33.3511 5.75195L24.6161 4.99023L23.3913 7.81355L32.1247 8.57526C32.3849 8.5824 32.6406 8.64462 32.8749 8.75781C33.1093 8.871 33.317 9.0326 33.4843 9.23193C33.6517 9.43127 33.7749 9.66382 33.8458 9.91425C33.9167 10.1647 33.9337 10.4273 33.8956 10.6848L32.9375 21.7544L29.5968 15.2072L23.5286 19.7599L24.6848 22.208L19.7344 31.949L30.9925 32.9311C32.0214 33.0175 33.0426 32.6934 33.8334 32.0296C34.6241 31.3657 35.1201 30.4159 35.2131 29.3876L36.893 9.96616C36.9795 8.93729 36.6553 7.91601 35.9915 7.12527C35.3276 6.33452 34.3778 5.83853 33.3495 5.74557L33.3511 5.75195Z"
                        fill="url(#paint1_linear_4234_32212)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_4234_32212"
                          x1="6.92016"
                          y1="4.85449"
                          x2="6.92016"
                          y2="33.4611"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#EBECFF" />
                          <stop offset="1" stopColor="#C0C4FF" />
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear_4234_32212"
                          x1="24.7852"
                          y1="4.99023"
                          x2="24.7852"
                          y2="32.9448"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#EBECFF" />
                          <stop offset="1" stopColor="#C0C4FF" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <svg
                      className="absolute right-[-10px] top-[18px]"
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                    >
                      <circle cx="8.43776" cy="8.43776" r="8.43776" fill="#5964F3" />
                      <path d="M8.4375 3.61597V9.64293" stroke="white" strokeWidth="1.80809" strokeLinecap="round" />
                      <circle cx="8.43725" cy="12.9578" r="0.904045" fill="white" />
                    </svg>
                  </div>
                  图片已失效
                </div>
              ) : (
                <Image
                  width={100}
                  height={90}
                  style={{ borderRadius: '6px' }}
                  className="cursonCase"
                  src={material.url}
                  preview={{ src: material.url }}
                  alt={material.name || '图片'}
                />
              )}
            </div>
          )
        } else if (material.type === 'video') {
          // 视频展示 - 直接展示 video 标签，点击播放
          return isExpired ? (
            <div
              key={`material-video-${index}`}
              className={`expired_video flexRow flexCol ${index > 0 ? 'mt-[10px]' : ''}`}
            >
              <svg
                className="mb-[18px]"
                xmlns="http://www.w3.org/2000/svg"
                width="68"
                height="46"
                viewBox="0 0 68 46"
                fill="none"
              >
                <path
                  d="M31.8602 9.41734L26.6575 16.1831L26.0692 15.943C25.4564 15.6809 24.7345 15.6214 24.1374 15.6532C22.2984 15.7511 20.7199 16.5609 20.6795 18.1008L20.6825 18.2894L21.3252 30.3651L17.3275 42.8101L5.86163 43.4203C3.87746 43.5259 2.28148 42.454 2.08933 40.923L2.07278 40.7433L0.494564 11.0865L31.8602 9.41734ZM16.2242 0.929584L24.8282 8.62657L15.5754 9.11897L6.97014 1.42205L16.2242 0.929584ZM27.8411 0.311379L32.7719 4.72288L32.0888 8.24019L27.1911 8.50083L18.587 0.803841L27.8411 0.311379ZM4.17628 1.57072L13.2113 9.24477L0.432743 9.9248L0.162279 4.84241C0.0736282 3.17654 1.53502 1.80681 3.5667 1.60899L3.7964 1.59094L4.17628 1.57072ZM33.689 0.000174794L33.3252 1.87651L31.3656 0.123816L33.689 0.000174794Z"
                  fill="url(#paint0_linear_4234_32202)"
                />
                <path
                  d="M57.8335 9.30465L58.4836 38.9974C58.5179 40.5652 57.0813 41.8092 55.1328 41.9403L54.9061 41.9499L19.0026 42.736L23.6681 34.0452C24.2957 34.3293 25.0781 34.4577 25.9027 34.4396C26.5308 34.4258 27.0642 34.3397 27.5851 34.1119L27.8074 34.0058L38.1587 27.2429C40.4607 26.1453 40.4697 23.5269 38.2525 22.4386L38.0527 22.3464L32.1247 18.2862L33.5367 15.6547L39.2246 9.71206L57.8335 9.30465ZM42.9626 0.801897L50.8297 8.29437L41.5648 8.49722L39.661 6.6841L40.2349 2.70827L42.9626 0.801897ZM53.9383 0.0810255C55.9562 0.0368462 57.5416 1.23324 57.6868 2.86959L57.6967 3.05322L57.8081 8.14159L53.3791 8.23856L44.8362 0.280304L53.9394 0.0810001L53.9383 0.0810255Z"
                  fill="url(#paint1_linear_4234_32202)"
                />
                <circle cx="56.6135" cy="34.5969" r="11.008" fill="#5964F3" />
                <path d="M56.6133 28.3064V36.1693" stroke="white" strokeWidth="2.35886" strokeLinecap="round" />
                <circle cx="56.613" cy="40.4939" r="1.17943" fill="white" />
                <defs>
                  <linearGradient
                    id="paint0_linear_4234_32202"
                    x1="27.8806"
                    y1="0.309277"
                    x2="30.1061"
                    y2="42.1301"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#EBECFF" />
                    <stop offset="1" stopColor="#C0C4FF" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_4234_32202"
                    x1="29.7169"
                    y1="0.611322"
                    x2="30.6336"
                    y2="42.4813"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#EBECFF" />
                    <stop offset="1" stopColor="#C0C4FF" />
                  </linearGradient>
                </defs>
              </svg>
              视频已失效
            </div>
          ) : material?.is_valid === 0 ? (
            <div className="flexCol" key={`material-video-${index}`}>
              <span className="expired_video_sys_title mb-[12px]">
                你的视频生成好啦。视频文件仅保存6个月，请及时下载。
              </span>
              <div className={`expired_video_sys flexRow flexCol ${index > 0 ? 'mt-[10px]' : ''}`}>
                <svg
                  className="mb-[18px]"
                  xmlns="http://www.w3.org/2000/svg"
                  width="68"
                  height="46"
                  viewBox="0 0 68 46"
                  fill="none"
                >
                  <path
                    d="M31.8602 9.41734L26.6575 16.1831L26.0692 15.943C25.4564 15.6809 24.7345 15.6214 24.1374 15.6532C22.2984 15.7511 20.7199 16.5609 20.6795 18.1008L20.6825 18.2894L21.3252 30.3651L17.3275 42.8101L5.86163 43.4203C3.87746 43.5259 2.28148 42.454 2.08933 40.923L2.07278 40.7433L0.494564 11.0865L31.8602 9.41734ZM16.2242 0.929584L24.8282 8.62657L15.5754 9.11897L6.97014 1.42205L16.2242 0.929584ZM27.8411 0.311379L32.7719 4.72288L32.0888 8.24019L27.1911 8.50083L18.587 0.803841L27.8411 0.311379ZM4.17628 1.57072L13.2113 9.24477L0.432743 9.9248L0.162279 4.84241C0.0736282 3.17654 1.53502 1.80681 3.5667 1.60899L3.7964 1.59094L4.17628 1.57072ZM33.689 0.000174794L33.3252 1.87651L31.3656 0.123816L33.689 0.000174794Z"
                    fill="url(#paint0_linear_4234_32202)"
                  />
                  <path
                    d="M57.8335 9.30465L58.4836 38.9974C58.5179 40.5652 57.0813 41.8092 55.1328 41.9403L54.9061 41.9499L19.0026 42.736L23.6681 34.0452C24.2957 34.3293 25.0781 34.4577 25.9027 34.4396C26.5308 34.4258 27.0642 34.3397 27.5851 34.1119L27.8074 34.0058L38.1587 27.2429C40.4607 26.1453 40.4697 23.5269 38.2525 22.4386L38.0527 22.3464L32.1247 18.2862L33.5367 15.6547L39.2246 9.71206L57.8335 9.30465ZM42.9626 0.801897L50.8297 8.29437L41.5648 8.49722L39.661 6.6841L40.2349 2.70827L42.9626 0.801897ZM53.9383 0.0810255C55.9562 0.0368462 57.5416 1.23324 57.6868 2.86959L57.6967 3.05322L57.8081 8.14159L53.3791 8.23856L44.8362 0.280304L53.9394 0.0810001L53.9383 0.0810255Z"
                    fill="url(#paint1_linear_4234_32202)"
                  />
                  <circle cx="56.6135" cy="34.5969" r="11.008" fill="#5964F3" />
                  <path d="M56.6133 28.3064V36.1693" stroke="white" strokeWidth="2.35886" strokeLinecap="round" />
                  <circle cx="56.613" cy="40.4939" r="1.17943" fill="white" />
                  <defs>
                    <linearGradient
                      id="paint0_linear_4234_32202"
                      x1="27.8806"
                      y1="0.309277"
                      x2="30.1061"
                      y2="42.1301"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#EBECFF" />
                      <stop offset="1" stopColor="#C0C4FF" />
                    </linearGradient>
                    <linearGradient
                      id="paint1_linear_4234_32202"
                      x1="29.7169"
                      y1="0.611322"
                      x2="30.6336"
                      y2="42.4813"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#EBECFF" />
                      <stop offset="1" stopColor="#C0C4FF" />
                    </linearGradient>
                  </defs>
                </svg>
                视频已失效
              </div>
            </div>
          ) : (
            <>
              {type !== 'user' && (
                <div className="expired_video_sys_title mb-[12px]">
                  你的视频生成好啦。视频文件仅保存6个月，请及时下载。
                </div>
              )}
              <MaterialVideoPlayer
                thumbnail_url={material?.thumbnail_url}
                key={`material-video-${index}`}
                videoUrl={material.url}
                hasMarginTop={index > 0}
              />
            </>
          )
        } else if (material.type === 'audio') {
          // 音频展示
          const audioFileType = getFileType(material.name)
          const currentAudioId = audioIds[audioIndex++]

          return (
            <div
              key={`material-audio-${index}`}
              className={`flexRowStart usr-file-file-box ${index > 0 ? 'mt-[10px]' : ''}`}
            >
              <img src={getFileIconByFileName(material.name)} alt="" className="mr-[12px] h-[40px] w-[40px]" />
              <div className="relative">
                <Tooltip title={material.name}>
                  <div className={`usr-uploaded-file-filename ${isExpired ? 'flexCol' : ''}`} style={{ width: 208 }}>
                    {formatFileName(material.name)}
                    {isExpired && <span className="expired_audio">音频已失效</span>}
                  </div>
                </Tooltip>
              </div>
              {/* 音频播放控制 */}
              {['wav', 'mp3', 'm4a', 'ogg', 'opus', 'aac'].includes(audioFileType) && !isExpired && (
                <MaterialAudioPlayer
                  audioUrl={material.url}
                  audioId={currentAudioId}
                  audioPlayingId={audioPlayingId}
                  setAudioPlayingId={setAudioPlayingId}
                />
              )}
              {['amr'].includes(audioFileType) && !isExpired && <AmrPlayer audioUrl={material.url} />}
            </div>
          )
        }
        return null
      })}
    </div>
  )
}

/**
 * 视频播放器组件
 * 初始展示首帧图，点击播放图标后加载并播放视频
 */
function MaterialVideoPlayer({
  thumbnail_url,
  videoUrl,
  hasMarginTop
}: {
  thumbnail_url?: string
  videoUrl: string
  hasMarginTop: boolean
}) {
  const [showVideo, setShowVideo] = useState(false)
  const [videoSize, setVideoSize] = useState<{ width: number; height: number } | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const thumbnailRef = useRef<HTMLImageElement>(null)

  // 获取首帧图尺寸
  const handleThumbnailLoad = () => {
    if (thumbnailRef.current) {
      const img = thumbnailRef.current
      const aspectRatio = img.naturalWidth / img.naturalHeight

      // 固定高度为160px
      const displayHeight = 160
      let displayWidth: number

      // 只使用两个宽度：240px 和 284px
      // 16:9比例 ≈ 1.778，显示 284×160
      // 其他所有比例（包括3:2、1:1等），默认显示 240×160
      if (Math.abs(aspectRatio - 16 / 9) < 0.1) {
        // 16:9比例
        displayWidth = 284
      } else {
        // 其他所有比例，默认显示 240×160
        displayWidth = 240
      }

      setVideoSize({ width: displayWidth, height: displayHeight })
    }
  }

  const handlePlayClick = () => {
    setShowVideo(true)
    // 延迟播放，确保视频元素已渲染
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play()
      }
    }, 100)
  }

  // 如果没有首帧图，直接显示视频
  // 兼容不同宽高比：使用最大宽高限制，让视频保持原始比例自适应显示
  // 最大宽度284px（16:9时高度160px），最大高度160px（3:2时宽度240px）
  if (!thumbnail_url) {
    return (
      <div className={hasMarginTop ? 'mt-[10px]' : ''}>
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          className="cursor-pointer rounded-md"
          style={{
            maxHeight: '160px',
            maxWidth: '240px',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block'
          }}
        />
      </div>
    )
  }

  return (
    <div className={hasMarginTop ? 'mt-[10px]' : ''}>
      {!showVideo ? (
        <div
          className="relative inline-block cursor-pointer"
          style={{
            width: videoSize ? `${videoSize.width}px` : 'auto',
            height: videoSize ? `${videoSize.height}px` : 'auto',
            maxHeight: '160px',
            maxWidth: '284px'
          }}
          onClick={handlePlayClick}
        >
          <img
            ref={thumbnailRef}
            src={thumbnail_url}
            alt="thumbnail"
            className="block rounded-md object-contain"
            style={{
              width: videoSize ? `${videoSize.width}px` : 'auto',
              height: videoSize ? `${videoSize.height}px` : 'auto',
              maxHeight: '160px',
              maxWidth: '284px',
              objectFit: 'contain',
              display: 'block'
            }}
            onLoad={handleThumbnailLoad}
          />
          <svg
            className="absolute bottom-0 left-0 right-0 top-0 m-auto"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="28"
            viewBox="0 0 24 28"
            fill="none"
          >
            <path
              d="M0 25.0722V2.00315C0 0.421985 1.74818 -0.53391 3.07933 0.31939L23.0725 13.1355C24.3711 13.968 24.2784 15.895 22.9058 16.5989L2.91263 26.8518C1.58174 27.5343 0 26.5679 0 25.0722Z"
              fill="white"
            />
          </svg>
        </div>
      ) : (
        <div
          className="relative inline-block"
          style={{
            width: videoSize ? `${videoSize.width}px` : 'auto',
            height: videoSize ? `${videoSize.height}px` : 'auto',
            maxHeight: '160px',
            maxWidth: '284px'
          }}
        >
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            poster={thumbnail_url}
            className="cursor-pointer rounded-md"
            style={{
              width: videoSize ? `${videoSize.width}px` : 'auto',
              height: videoSize ? `${videoSize.height}px` : 'auto',
              maxHeight: '160px',
              maxWidth: '284px',
              objectFit: 'contain',
              display: 'block'
            }}
            autoPlay
            preload="metadata"
          />
        </div>
      )}
    </div>
  )
}

/**
 * 音频播放器组件（用于 material_info 中的音频）
 */
function MaterialAudioPlayer({
  audioUrl,
  audioId,
  audioPlayingId,
  setAudioPlayingId
}: {
  audioUrl: string
  audioId: string
  audioPlayingId: string | null
  setAudioPlayingId: (id: string | null) => void
}) {
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (audioPlayingId !== audioId && audioRef.current) {
      audioRef.current.pause()
    }
  }, [audioPlayingId, audioId])

  return (
    <>
      {audioPlayingId !== audioId && (
        <span
          className="cursor-pointer"
          onClick={() => {
            if (audioRef.current) {
              audioRef.current.play()
            }
          }}
        >
          {AudioPlaySvg}
        </span>
      )}
      {audioPlayingId === audioId && (
        <div className="group relative">
          <div className="audio-running-div block group-hover:hidden">{RunningSvg}</div>
          <div
            className="hidden cursor-pointer group-hover:block"
            onClick={() => {
              if (audioRef.current) {
                audioRef.current.pause()
              }
            }}
          >
            {PauseSvg}
          </div>
        </div>
      )}
      <audio
        ref={audioRef}
        src={audioUrl}
        style={{ display: 'none' }}
        onPlay={() => setAudioPlayingId(audioId)}
        onPause={() => {
          if (audioPlayingId === audioId) setAudioPlayingId(null)
        }}
        onEnded={() => setAudioPlayingId(null)}
      />
    </>
  )
}
